import { useState, useEffect } from 'react';
import axios from "axios";
import { findAllTurmasAcao, gerarTurmasAcao, duplicarTurma, inactivateTurma, findTurmasRegraEspecialAcao, marcarTurmasRegraEspecial } from "../../../../API/apiAcoes";
import { TurmaAcao } from "../../../../types/acao";

// Create the provider component
export const useTurma = (acaoId?: string | null) => {
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);
  const [loadingRegraEspecial, setLoadingRegraEspecial] = useState<boolean>(false);
  const [turmasAcao, setTurmasAcao] = useState<TurmaAcao[]>([]);
  const [turmasRegraEspecialIds, setTurmasRegraEspecialIds] = useState<number[]>([]);

  useEffect(() => {
    if (acaoId) {
      consultarTurmasAcao(acaoId);
      consultarTurmasRegraEspecial(acaoId);
    }
  },[acaoId]);

  useEffect(() => {
    if (!msgSuccess) return;

    const timer = setTimeout(() => {
      setMsgSuccess(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;

    const timer = setTimeout(() => {
      setMsgError(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [msgError]);

  const consultarTurmasAcao = async (acaoId: string) => {
    setLoading(true);
    try {
      const result = await findAllTurmasAcao(acaoId);
      setTurmasAcao(result);
    } catch (error) {
      setMsgError('Erro ao listar as turmas da ação');
    } finally {
      setLoading(false);
    }
  };

  const gerarTurmas = async (qtdTurmas: number) => {
    if (!acaoId) return;
    setLoadingSave(true);
    try {
      const result = await gerarTurmasAcao(acaoId, qtdTurmas);
      if (result.success) {
        setMsgSuccess('Turma(s) gerada(s) com sucesso!');
        await consultarTurmasAcao(acaoId);
      } else {
        setMsgError('Erro ao gerar turma(s) da ação');
      }
    } catch (error) {
      setMsgError('Erro ao gerar turma(s) da ação');
    } finally {
      setLoadingSave(false);
    }
  };

  const consultarTurmasRegraEspecial = async (idAcao: string) => {
    try {
      const turmaIds = await findTurmasRegraEspecialAcao(idAcao);
      setTurmasRegraEspecialIds(turmaIds);
    } catch (error) {
      setTurmasRegraEspecialIds([]);
    }
  };

  const parseApiErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as {
        message?: string;
        errors?: Record<string, string[]>;
      } | undefined;

      const errorMessages = Object.values(data?.errors ?? {}).flatMap((messages) =>
        Array.isArray(messages) ? messages : []
      );

      if (errorMessages.length > 0) {
        return errorMessages.join(" ");
      }

      if (data?.message) {
        return data.message;
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallbackMessage;
  };

  const salvarTurmasRegraEspecial = async (turmaIds: number[]) => {
    if (!acaoId) return false;

    setLoadingRegraEspecial(true);
    try {
      const result = await marcarTurmasRegraEspecial(acaoId, turmaIds);

      if (result?.success === false) {
        setMsgError(result?.message ?? "Erro ao marcar turmas como regra especial");
        return false;
      }

      setMsgSuccess("Regra especial salva com sucesso!");
      setMsgError(null);
      await consultarTurmasRegraEspecial(acaoId);
      return true;
    } catch (error) {
      setMsgError(parseApiErrorMessage(error, "Erro ao marcar turmas como regra especial"));
      return false;
    } finally {
      setLoadingRegraEspecial(false);
    }
  };

  const duplicarTurmaAcao = async (turmaId: string) => {
    if (!acaoId) return;
    setLoadingConfirm(true);
    try {
      await duplicarTurma(turmaId);
      setMsgSuccess('Turma duplicada com sucesso!');
      await consultarTurmasAcao(acaoId);
    } catch (error) {
      setMsgError('Erro ao duplicar turma');
    } finally {
      setLoadingConfirm(false);
    }
  };

  const excluirTurmaAcao = async (turmaId: string) => {
    if (!acaoId) return;
    setLoadingConfirm(true);
    try {
      await inactivateTurma(turmaId);
      setMsgSuccess('Turma excluída com sucesso!');
      await consultarTurmasAcao(acaoId);
    } catch (error) {
      setMsgError('Erro ao excluir turma');
    } finally {
      setLoadingConfirm(false);
    }
  };

  return {
    msgError,
    setMsgError,
    msgSuccess,
    setMsgSuccess,
    loading,
    loadingSave,
    loadingConfirm,
    loadingRegraEspecial,
    turmasAcao,
    turmasRegraEspecialIds,
    gerarTurmas,
    consultarTurmasAcao,
    consultarTurmasRegraEspecial,
    duplicarTurmaAcao,
    excluirTurmaAcao,
    salvarTurmasRegraEspecial,
  };

};
